import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Loader2,
  Save,
  Trash2,
  Pencil,
  X,
  Building2,
  Search,
  FolderKanban,
  MapPin,
  Hash,
  Tag,
  Upload,
  Image as ImageIcon,
  ExternalLink,
  Copy,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import StatusesPanel from "@/components/StatusesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const UF_LIST = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA",
  "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN",
  "RO", "RR", "RS", "SC", "SE", "SP", "TO",
];

const emptyOrg = () => ({
  id: null,
  trade_name: "",
  legal_name: "",
  cnpj: "",
  founded_date: "",
  state_registration: "",
  municipal_registration: "",
  state_exempt: false,
  tax_regime: "Simples Nacional",
  zip: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "SP",
  country: "Brasil",
  phone: "",
  email: "",
});

const emptyDep = () => ({ id: null, name: "", description: "", organization_id: "" });

const formatCNPJ = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 14);
  if (d.length < 14) return d;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
};

const formatCEP = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  if (d.length < 8) return d;
  return `${d.slice(0, 5)}-${d.slice(5, 8)}`;
};

/* ───────────────────────── ORGANIZATIONS ───────────────────────── */
const OrganizationsPanel = () => {
  const [items, setItems] = useState([]);
  const [taxRegimes, setTaxRegimes] = useState(["Simples Nacional", "Lucro Presumido", "Lucro Real", "MEI", "Outro"]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lookingUpCep, setLookingUpCep] = useState(false);
  const [lookingUpCnpj, setLookingUpCnpj] = useState(false);
  const [rfbInfo, setRfbInfo] = useState(null);
  const [search, setSearch] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [copiedCareerLink, setCopiedCareerLink] = useState(false);
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [orgs, regimes] = await Promise.all([
        axios.get(`${API}/registry/organizations`),
        axios.get(`${API}/registry/organizations/tax-regimes`).catch(() => null),
      ]);
      setItems(orgs.data || []);
      if (regimes?.data) setTaxRegimes(regimes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((o) =>
      [o.trade_name, o.legal_name, o.cnpj, o.city]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [items, search]);

  const handleCnpjLookup = async () => {
    if (!editing) return;
    const digits = (editing.cnpj || "").replace(/\D/g, "");
    if (digits.length !== 14) {
      toast.error("CNPJ precisa ter 14 dígitos");
      return;
    }
    setLookingUpCnpj(true);
    setRfbInfo(null);
    try {
      const res = await axios.get(`${API}/registry/organizations/lookup-cnpj/${digits}`);
      const d = res.data || {};
      setEditing((p) => ({
        ...p,
        // Only fill empty fields or always replace? Replace to match RFB.
        trade_name: d.trade_name || p.trade_name,
        legal_name: d.legal_name || p.legal_name,
        cnpj: d.cnpj || p.cnpj,
        founded_date: d.founded_date || p.founded_date,
        state_registration: d.state_registration || p.state_registration,
        state_exempt: d.state_exempt ?? p.state_exempt,
        tax_regime: d.tax_regime || p.tax_regime,
        zip: d.zip || p.zip,
        street: d.street || p.street,
        number: d.number || p.number,
        complement: d.complement || p.complement,
        neighborhood: d.neighborhood || p.neighborhood,
        city: d.city || p.city,
        state: d.state || p.state,
        phone: d.phone || p.phone,
        email: d.email || p.email,
      }));
      setRfbInfo({ status: d._rfb_status || "", cnae: d._rfb_cnae || "" });
      toast.success("Dados da Receita preenchidos!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao consultar CNPJ";
      toast.error(msg);
    } finally {
      setLookingUpCnpj(false);
    }
  };

  const handleCepLookup = async () => {
    if (!editing) return;
    const cep = (editing.zip || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setLookingUpCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await res.json();
      if (d.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      setEditing((p) => ({
        ...p,
        street: p.street || d.logradouro || "",
        neighborhood: p.neighborhood || d.bairro || "",
        city: p.city || d.localidade || "",
        state: (p.state && p.state !== "SP") ? p.state : (d.uf || p.state),
      }));
    } catch {
      toast.error("Falha ao consultar CEP");
    } finally {
      setLookingUpCep(false);
    }
  };

  const handleSave = async () => {
    if (!editing.trade_name.trim() || !editing.legal_name.trim() || !editing.cnpj) {
      toast.error("Preencha Nome Fantasia, Razão Social e CNPJ");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (editing.id) {
        await axios.put(`${API}/registry/organizations/${editing.id}`, payload);
        toast.success("Empresa atualizada");
      } else {
        await axios.post(`${API}/registry/organizations`, payload);
        toast.success("Empresa cadastrada");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Excluir a empresa "${org.trade_name}"?`)) return;
    try {
      await axios.delete(`${API}/registry/organizations/${org.id}`);
      toast.success("Empresa excluída");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível excluir");
    }
  };

  const careerPublicUrl = (slug) =>
    slug && typeof window !== "undefined"
      ? `${window.location.origin}/carreiras/org/${slug}`
      : "";

  const handleLogoUpload = async (file) => {
    if (!file || !editing?.id) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Envie PNG, JPG ou WEBP");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx. 2 MB)");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const res = await axios.post(
        `${API}/registry/organizations/${editing.id}/logo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setEditing((p) => ({ ...p, ...res.data }));
      toast.success("Logo atualizado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!editing?.id || !editing.logo_url) return;
    if (!window.confirm("Remover o logo atual?")) return;
    setUploadingLogo(true);
    try {
      const res = await axios.delete(
        `${API}/registry/organizations/${editing.id}/logo`
      );
      setEditing((p) => ({ ...p, ...res.data }));
      toast.success("Logo removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCoverUpload = async (file) => {
    if (!file || !editing?.id) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Envie PNG, JPG ou WEBP");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx. 4 MB)");
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const res = await axios.post(
        `${API}/registry/organizations/${editing.id}/cover`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setEditing((p) => ({ ...p, ...res.data }));
      toast.success("Banner atualizado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar banner");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCoverRemove = async () => {
    if (!editing?.id || !editing.cover_url) return;
    if (!window.confirm("Remover o banner atual?")) return;
    setUploadingCover(true);
    try {
      const res = await axios.delete(
        `${API}/registry/organizations/${editing.id}/cover`
      );
      setEditing((p) => ({ ...p, ...res.data }));
      toast.success("Banner removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover banner");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCopyCareerLink = async () => {
    const url = careerPublicUrl(editing?.slug);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCareerLink(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopiedCareerLink(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando empresas…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!editing && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-slate-500 max-w-xl">
            Cadastre as empresas para as quais você recruta. Cada vaga pode ser vinculada a
            uma empresa — útil quando há filiais, clientes ou grupos econômicos distintos.
          </p>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, CNPJ ou cidade…"
                data-testid="orgs-search-input"
                className="pl-8 w-64 h-9 text-sm"
              />
            </div>
            <Button
              onClick={() => setEditing(emptyOrg())}
              data-testid="new-org-button"
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nova empresa
            </Button>
          </div>
        </div>
      )}

      {!editing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-sm text-slate-500 py-12 border border-dashed border-slate-200 rounded-lg bg-slate-50">
              {search ? `Nenhuma empresa encontrada para "${search}"` : "Nenhuma empresa cadastrada ainda."}
            </div>
          )}
          {filtered.map((o) => (
            <div
              key={o.id}
              data-testid={`org-card-${o.id}`}
              className="bg-white border border-slate-200 rounded-lg p-4 flex gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-brand-blue shrink-0 flex items-center justify-center overflow-hidden">
                {o.logo_url ? (
                  <img
                    src={o.logo_url}
                    alt={`Logo ${o.trade_name}`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Building2 className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{o.trade_name}</div>
                    <div className="text-xs text-slate-500 truncate">{o.legal_name}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing(o)}
                      data-testid={`edit-org-${o.id}`}
                      aria-label="Editar"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(o)}
                      data-testid={`delete-org-${o.id}`}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      aria-label="Excluir"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Hash className="w-3 h-3" /> {formatCNPJ(o.cnpj)}
                  </span>
                  {(o.city || o.state) && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {o.city}{o.state ? ` / ${o.state}` : ""}
                    </span>
                  )}
                  {o.tax_regime && (
                    <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                      {o.tax_regime}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="heading text-base font-semibold text-slate-900">
              {editing.id ? "Editar empresa" : "Cadastro de empresa"}
            </h3>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid="cancel-org-edit">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Identidade visual & Carreiras (apenas para empresas já cadastradas) */}
          {editing.id && (
            <section className="space-y-3 pb-5 border-b border-slate-100">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Identidade visual & página de carreiras
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5">
                {/* Logo */}
                <div className="flex flex-col gap-2">
                  <Label className="text-xs">Logo da empresa</Label>
                  <div className="flex items-start gap-3">
                    <div className="w-20 h-20 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                      {editing.logo_url ? (
                        <img
                          src={editing.logo_url}
                          alt="Logo"
                          className="w-full h-full object-contain"
                          data-testid="org-logo-preview"
                        />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-slate-300" />
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        data-testid="org-logo-input"
                        onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        data-testid="org-logo-upload-btn"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Upload className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        {editing.logo_url ? "Trocar" : "Enviar logo"}
                      </Button>
                      {editing.logo_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={handleLogoRemove}
                          disabled={uploadingLogo}
                          data-testid="org-logo-delete-btn"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Remover
                        </Button>
                      )}
                      <p className="text-[10px] text-slate-500 leading-relaxed max-w-[180px]">
                        PNG, JPG ou WEBP. Ideal quadrado, até 2 MB.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Career public link */}
                <div className="space-y-2">
                  <Label className="text-xs">Página pública de carreiras</Label>
                  <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center gap-2">
                    <code
                      className="flex-1 text-xs text-slate-700 font-mono truncate"
                      data-testid="org-career-url"
                    >
                      {careerPublicUrl(editing.slug) || "Salve a empresa para gerar o link"}
                    </code>
                    {editing.slug && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={handleCopyCareerLink}
                          className="h-7"
                          data-testid="org-career-copy"
                        >
                          {copiedCareerLink ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            window.open(
                              careerPublicUrl(editing.slug),
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          className="h-7"
                          data-testid="org-career-open"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Todas as vagas desta empresa (com <em>organization_id</em> vinculado)
                    aparecem nesse link. Candidatos veem o nome e o logo aqui cadastrados —
                    não o nome do seu usuário.
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Slug: <span className="font-mono">{editing.slug || "—"}</span>{" "}
                    <span>· {editing.jobs_count ?? 0} vaga(s) vinculada(s)</span>
                  </p>
                </div>
              </div>

              {/* Banner / cover image */}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">
                    Banner da página de carreiras{" "}
                    <span className="text-slate-400 font-normal">(opcional)</span>
                  </Label>
                  <div className="flex gap-1.5">
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      data-testid="org-cover-input"
                      onChange={(e) => handleCoverUpload(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      data-testid="org-cover-upload-btn"
                      className="h-8"
                    >
                      {uploadingCover ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {editing.cover_url ? "Trocar" : "Enviar banner"}
                    </Button>
                    {editing.cover_url && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={handleCoverRemove}
                        disabled={uploadingCover}
                        data-testid="org-cover-delete-btn"
                        className="h-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  className="relative w-full rounded-lg border border-slate-200 bg-slate-50 overflow-hidden"
                  style={{ aspectRatio: "4 / 1" }}
                  data-testid="org-cover-preview"
                >
                  {editing.cover_url ? (
                    <img
                      src={editing.cover_url}
                      alt="Banner"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
                      <ImageIcon className="w-6 h-6 mr-2" />
                      Nenhum banner enviado — usaremos o gradiente padrão.
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Recomendado: <strong>1920 × 480 px</strong> (PNG, JPG ou WEBP, até 4 MB).
                  Uma foto real do ambiente/time transmite cultura e costuma
                  aumentar o volume de candidaturas.
                </p>
              </div>
            </section>
          )}

          {/* Dados básicos */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Dados básicos
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Fantasia *</Label>
                <Input
                  value={editing.trade_name}
                  onChange={(e) => setEditing({ ...editing, trade_name: e.target.value })}
                  data-testid="org-trade-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Razão Social *</Label>
                <Input
                  value={editing.legal_name}
                  onChange={(e) => setEditing({ ...editing, legal_name: e.target.value })}
                  data-testid="org-legal-name"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  CNPJ *
                  <span className="text-[10px] font-normal text-slate-500 ml-2">
                    (busque na Receita)
                  </span>
                </Label>
                <div className="flex gap-1.5">
                  <Input
                    value={formatCNPJ(editing.cnpj)}
                    onChange={(e) => setEditing({ ...editing, cnpj: e.target.value.replace(/\D/g, "") })}
                    onBlur={() => {
                      if ((editing.cnpj || "").length === 14 && !editing.id && !editing.legal_name) {
                        handleCnpjLookup();
                      }
                    }}
                    placeholder="00.000.000/0000-00"
                    data-testid="org-cnpj"
                    maxLength={18}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCnpjLookup}
                    disabled={lookingUpCnpj || (editing.cnpj || "").length !== 14}
                    className="shrink-0"
                    data-testid="org-cnpj-lookup-button"
                    aria-label="Buscar na Receita"
                    title="Consultar Receita e preencher automaticamente"
                  >
                    {lookingUpCnpj
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Search className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                {rfbInfo && (
                  <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 mt-1 space-y-0.5">
                    {rfbInfo.status && (
                      <div>
                        <strong>Situação cadastral:</strong> {rfbInfo.status}
                      </div>
                    )}
                    {rfbInfo.cnae && (
                      <div>
                        <strong>CNAE principal:</strong> {rfbInfo.cnae}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Data de Constituição</Label>
                <Input
                  type="date"
                  value={editing.founded_date || ""}
                  onChange={(e) => setEditing({ ...editing, founded_date: e.target.value })}
                  data-testid="org-founded-date"
                />
              </div>
            </div>
          </section>

          {/* Inscrições e regime */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Inscrições e regime
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label>Inscrição Estadual</Label>
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <Checkbox
                      checked={editing.state_exempt}
                      onCheckedChange={(v) =>
                        setEditing({ ...editing, state_exempt: v === true })
                      }
                      data-testid="org-state-exempt"
                    />
                    Isento
                  </label>
                </div>
                <Input
                  value={editing.state_registration}
                  onChange={(e) => setEditing({ ...editing, state_registration: e.target.value })}
                  placeholder={editing.state_exempt ? "ISENTO" : ""}
                  disabled={editing.state_exempt}
                  data-testid="org-state-registration"
                />
              </div>
              <div className="space-y-2">
                <Label>Inscrição Municipal</Label>
                <Input
                  value={editing.municipal_registration}
                  onChange={(e) => setEditing({ ...editing, municipal_registration: e.target.value })}
                  data-testid="org-municipal-registration"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Regime Tributário *</Label>
                <Select
                  value={editing.tax_regime}
                  onValueChange={(v) => setEditing({ ...editing, tax_regime: v })}
                >
                  <SelectTrigger data-testid="org-tax-regime">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {taxRegimes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Endereço */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Endereço
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label>CEP *</Label>
                <div className="flex gap-1.5">
                  <Input
                    value={formatCEP(editing.zip)}
                    onChange={(e) => setEditing({ ...editing, zip: e.target.value.replace(/\D/g, "") })}
                    onBlur={handleCepLookup}
                    placeholder="00000-000"
                    maxLength={9}
                    data-testid="org-zip"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCepLookup}
                    disabled={lookingUpCep || !(editing.zip || "").length}
                    className="shrink-0"
                    aria-label="Buscar CEP"
                  >
                    {lookingUpCep ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>Logradouro *</Label>
                <Input
                  value={editing.street}
                  onChange={(e) => setEditing({ ...editing, street: e.target.value })}
                  data-testid="org-street"
                />
              </div>
              <div className="space-y-2">
                <Label>Número *</Label>
                <Input
                  value={editing.number}
                  onChange={(e) => setEditing({ ...editing, number: e.target.value })}
                  data-testid="org-number"
                />
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>Complemento</Label>
                <Input
                  value={editing.complement}
                  onChange={(e) => setEditing({ ...editing, complement: e.target.value })}
                  data-testid="org-complement"
                />
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>Bairro *</Label>
                <Input
                  value={editing.neighborhood}
                  onChange={(e) => setEditing({ ...editing, neighborhood: e.target.value })}
                  data-testid="org-neighborhood"
                />
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>Cidade *</Label>
                <Input
                  value={editing.city}
                  onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                  data-testid="org-city"
                />
              </div>
              <div className="md:col-span-1 space-y-2">
                <Label>UF *</Label>
                <Select
                  value={editing.state}
                  onValueChange={(v) => setEditing({ ...editing, state: v })}
                >
                  <SelectTrigger data-testid="org-state"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UF_LIST.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>País</Label>
                <Input
                  value={editing.country}
                  onChange={(e) => setEditing({ ...editing, country: e.target.value })}
                  data-testid="org-country"
                />
              </div>
            </div>
          </section>

          {/* Contato */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Contato
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={editing.phone}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  placeholder="(11) 0000-0000"
                  data-testid="org-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  placeholder="contato@empresa.com.br"
                  data-testid="org-email"
                />
              </div>
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-org-button"
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar empresa
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ───────────────────────── DEPARTMENTS ───────────────────────── */
const DepartmentsPanel = () => {
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [deps, orgs] = await Promise.all([
        axios.get(`${API}/registry/departments`),
        axios.get(`${API}/registry/organizations`),
      ]);
      setItems(deps.data || []);
      setOrgs(orgs.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      d.name.toLowerCase().includes(q) ||
      (d.organization_name || "").toLowerCase().includes(q)
    );
  });

  const handleSave = async () => {
    if (!editing.name.trim()) {
      toast.error("Informe o nome do departamento");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description,
        organization_id: editing.organization_id || "",
      };
      if (editing.id) {
        await axios.put(`${API}/registry/departments/${editing.id}`, payload);
        toast.success("Departamento atualizado");
      } else {
        await axios.post(`${API}/registry/departments`, payload);
        toast.success("Departamento cadastrado");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Excluir o departamento "${d.name}"?`)) return;
    try {
      await axios.delete(`${API}/registry/departments/${d.id}`);
      toast.success("Departamento excluído");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível excluir");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando departamentos…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!editing && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-slate-500 max-w-xl">
            Estruture os departamentos/áreas da sua empresa. Você poderá associar
            cada vaga a um departamento diretamente no formulário de criação.
          </p>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar departamento…"
                data-testid="deps-search-input"
                className="pl-8 w-56 h-9 text-sm"
              />
            </div>
            <Button
              onClick={() => setEditing(emptyDep())}
              data-testid="new-dep-button"
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Novo departamento
            </Button>
          </div>
        </div>
      )}

      {!editing && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Nome
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Empresa vinculada
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Descrição
                </th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">
                    {search.trim() ? `Nenhum resultado para "${search}"` : "Nenhum departamento cadastrado."}
                  </td>
                </tr>
              )}
              {filtered.map((d) => (
                <tr key={d.id} data-testid={`dep-row-${d.id}`} className="border-b border-slate-100">
                  <td className="px-4 py-3 align-middle">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <FolderKanban className="w-4 h-4" />
                      </div>
                      <div className="font-medium text-slate-900">{d.name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle text-slate-700">
                    {d.organization_name ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-brand-blue px-2 py-0.5 rounded">
                        <Building2 className="w-3 h-3" /> {d.organization_name}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-middle text-slate-500 text-xs max-w-sm truncate">
                    {d.description || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(d)} data-testid={`edit-dep-${d.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(d)}
                        data-testid={`delete-dep-${d.id}`}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="heading text-base font-semibold text-slate-900">
              {editing.id ? "Editar departamento" : "Novo departamento"}
            </h3>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                data-testid="dep-name-input"
                placeholder="Ex: Tecnologia, Marketing…"
              />
            </div>
            <div className="space-y-2">
              <Label>Empresa vinculada</Label>
              <Select
                value={editing.organization_id || "__none__"}
                onValueChange={(v) => setEditing({ ...editing, organization_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="dep-org-select">
                  <SelectValue placeholder="— Opcional —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sem vínculo —</SelectItem>
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.trade_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea
              rows={2}
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              data-testid="dep-description-input"
              placeholder="Contexto do departamento (opcional)"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-dep-button"
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── PAGE ─────────────────────────── */
const CadastrosPage = () => {
  const { can } = useAuth();
  const canOrgs = can("registry", "organizations");
  const canDeps = can("registry", "departments");
  const [tab, setTab] = useState(canOrgs ? "empresas" : canDeps ? "departamentos" : "status");

  const TAB_TITLES = {
    empresas: "Empresas",
    departamentos: "Departamentos",
    status: "Status do pipeline",
  };
  useDocumentTitle(`Cadastros — ${TAB_TITLES[tab] || ""}`);

  return (
    <div data-testid="cadastros-page" className="space-y-7">
      <div>
        <h1 className="heading text-2xl sm:text-3xl font-bold text-slate-900">Cadastros</h1>
        <p className="text-sm text-slate-500 mt-1">
          Mantenha empresas, departamentos e status do pipeline organizados para
          montar suas vagas rapidamente.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-1.5 inline-flex gap-1">
        {canOrgs && (
          <button
            type="button"
            onClick={() => setTab("empresas")}
            data-testid="cadastros-tab-empresas"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
              tab === "empresas" ? "bg-brand-blue text-white shadow-sm" : "text-slate-600 hover:text-brand-blue"
            }`}
          >
            <Building2 className="w-3.5 h-3.5" /> Empresas
          </button>
        )}
        {canDeps && (
          <button
            type="button"
            onClick={() => setTab("departamentos")}
            data-testid="cadastros-tab-departamentos"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
              tab === "departamentos" ? "bg-brand-blue text-white shadow-sm" : "text-slate-600 hover:text-brand-blue"
            }`}
          >
            <FolderKanban className="w-3.5 h-3.5" /> Departamentos
          </button>
        )}
        <button
          type="button"
          onClick={() => setTab("status")}
          data-testid="cadastros-tab-status"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
            tab === "status" ? "bg-brand-blue text-white shadow-sm" : "text-slate-600 hover:text-brand-blue"
          }`}
        >
          <Tag className="w-3.5 h-3.5" /> Status
        </button>
      </div>

      {tab === "empresas" && canOrgs && <OrganizationsPanel />}
      {tab === "departamentos" && canDeps && <DepartmentsPanel />}
      {tab === "status" && <StatusesPanel />}
    </div>
  );
};

export default CadastrosPage;
